import { useEffect, useId, useRef, type MouseEvent, type ReactNode } from 'react';
import { cn } from './cn';

/**
 * Props for the Modal component.
 */
export interface ModalProps {
  /** Whether the modal is visible. */
  open: boolean;
  /** Callback invoked when the modal should close (backdrop click, Escape key). */
  onClose: () => void;
  /** Title displayed in the modal header. */
  title: string;
  /** Content rendered inside the modal body. */
  children: ReactNode;
  /** Additional CSS classes for the dialog element. */
  className?: string;
}

/**
 * Accessible modal dialog built on the native HTML `<dialog>` element.
 * Closes on backdrop click and Escape key press.
 * Traps focus within the dialog while open.
 *
 * Uses the native `cancel` event to handle Escape key. A `isProgrammaticClose`
 * ref guards the native `close` event listener so that programmatic calls to
 * `dialog.close()` (when `open` prop turns false) do not trigger a duplicate
 * `onClose` invocation. Each instance generates a unique ID for
 * `aria-labelledby` to support multiple simultaneous modals.
 *
 * @param props.open - Controls visibility
 * @param props.onClose - Close handler (backdrop / Escape key)
 * @param props.title - Modal heading
 * @example
 * ```tsx
 * <Modal open={isOpen} onClose={() => setIsOpen(false)} title="Confirm action">
 *   <p>Are you sure?</p>
 *   <Button onClick={() => setIsOpen(false)}>Cancel</Button>
 * </Modal>
 * ```
 */
export function Modal({ open, onClose, title, children, className }: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  /**
   * Tracks whether the current close was initiated programmatically by the
   * effect below (i.e. `open` prop changed to false). When true the native
   * `close` event must not call `onClose` again to avoid double invocations.
   */
  const isProgrammaticClose = useRef(false);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog == null) return;

    if (open) {
      if (!dialog.open) {
        dialog.showModal();
      }
    } else {
      if (dialog.open) {
        isProgrammaticClose.current = true;
        dialog.close();
      }
    }
  }, [open]);

  // Native dialog fires `cancel` on Escape — we prevent the default close
  // and delegate to onClose so React state stays in sync.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog == null) return;

    function handleCancel(e: Event) {
      e.preventDefault();
      onClose();
    }

    dialog.addEventListener('cancel', handleCancel);
    return () => dialog.removeEventListener('cancel', handleCancel);
  }, [onClose]);

  // The native `close` event fires whenever the dialog is closed by any
  // mechanism (programmatic dialog.close(), form method="dialog", etc.).
  // Guard against duplicate calls when the effect above triggers the close.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog == null) return;

    function handleClose() {
      if (isProgrammaticClose.current) {
        isProgrammaticClose.current = false;
        return;
      }
      onClose();
    }

    dialog.addEventListener('close', handleClose);
    return () => dialog.removeEventListener('close', handleClose);
  }, [onClose]);

  function handleBackdropClick(e: MouseEvent<HTMLDialogElement>) {
    const rect = dialogRef.current?.getBoundingClientRect();
    if (rect == null) return;
    const { clientX, clientY } = e;
    const clickedOutside =
      clientX < rect.left || clientX > rect.right || clientY < rect.top || clientY > rect.bottom;
    if (clickedOutside) {
      onClose();
    }
  }

  return (
    <dialog
      ref={dialogRef}
      onClick={handleBackdropClick}
      className={cn(
        'w-full max-w-lg rounded-xl border border-ink/10 bg-card p-0 shadow-xl',
        // Constrain height and allow scrolling for long content
        'max-h-[85vh]',
        'backdrop:bg-black/60',
        // Remove default dialog outline
        'focus:outline-none',
        className,
      )}
      aria-labelledby={titleId}
    >
      {/* Stop click propagation inside content so backdrop check works */}
      <div
        className="flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
        role="presentation"
      >
        {/* Header */}
        <div className="flex flex-shrink-0 items-center justify-between border-b border-ink/10 px-6 py-4">
          <h2 id={titleId} className="text-lg font-semibold text-ink">
            {title}
          </h2>
          <button
            type="button"
            aria-label="Close modal"
            onClick={onClose}
            className="rounded p-1 text-ink-muted hover:text-ink focus:outline-none focus:ring-2 focus:ring-[var(--color-primary,theme(colors.blue.500))]"
          >
            <svg
              className="h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body — scrollable when content exceeds available height */}
        <div className="overflow-y-auto px-6 py-4">{children}</div>
      </div>
    </dialog>
  );
}
